import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  downloadBlob,
  EmptyState,
  ErrorState,
  formatCurrency,
  MutationError,
  ProblemError,
  Skeleton,
} from "@csa/web-foundation";
import { useT } from "../i18n/index";
import type { MessageKey } from "../i18n/dictionaries";
import { usePartyApi } from "../api/party-api";
import { useActiveProposals, useProposal, useRespond } from "./use-proposal";
import type {
  Proposal as ProposalT,
  ProposalOffer,
  QualityBand,
  ResponseKind,
} from "../api/contracts";

// C0 — the customer proposal page (the visible customer half of the loop). Renders the
// advisor's PRESENTED recommendation (N3 + N3.1): the recommended offer led & badged, the
// "why" rationale as the hero, the other offers secondary, the legal best-of-panel line —
// and lets the customer accept/defer/ask/decline via I1. Load-bearing invariant: the
// customer SIGNALS, the advisor BINDS. Accept → "advisor will confirm", NEVER "you're
// covered". No commission, no scoring/pricing in the browser, masked-only.

type TFn = (k: MessageKey) => string;

const BAND_STARS: Record<QualityBand, string> = {
  EXCELLENT: "★★★★★",
  STRONG: "★★★★☆",
  GOOD: "★★★☆☆",
  FAIR: "★★☆☆☆",
};

// The shared DS1.1 money formatter — TR dot-thousands (₺2.480), one place for every
// money surface. Display-only; the figure is the server's, honoured as-is.
function money(gross: string, currency: string): string {
  return formatCurrency(gross, { currency, locale: "tr" });
}

function dateTr(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// F1-F — in-app discovery. "Teklifiniz" (no deep-link) calls the F1-B list read and, if
// exactly one active proposal exists, renders its full by-id detail directly; none → the
// calm cold-start (also the graceful state before F1-B lands: a 404/error → empty, never a
// crash); several → a short pick-list. The customer finds their proposal from the app.
export function ProposalDiscovery() {
  const { t } = useT();
  const q = useActiveProposals();
  const items = q.data ?? [];

  if (q.isLoading) {
    return (
      <Card data-testid="proposal-loading">
        <Skeleton lines={6} />
      </Card>
    );
  }
  // A FAILED read is not "no proposals" — folding the two hid an outage behind the
  // calm cold-start, telling a customer with a live offer that they had none.
  // BUT a 404 genuinely IS "no active proposal" here: the N3 read answers a party
  // with nothing to show with a uniform 404 (and that is also what an
  // undeployed F1-B returns). So 404 stays the calm empty; only a real failure
  // — 5xx, or status 0 (offline) from networkProblemError — becomes an error.
  const isNotFound = q.error instanceof ProblemError && q.error.status === 404;
  if (q.error && !isNotFound) {
    return (
      <ErrorState>
        <div data-testid="proposal-error">
          <h1>{t("pp.errorTitle")}</h1>
          <p className="muted">{t("pp.errorBody")}</p>
        </div>
      </ErrorState>
    );
  }
  if (isNotFound || items.length === 0) {
    return (
      <EmptyState>
        <div data-testid="proposal-empty">
          <h1>{t("pp.emptyTitle")}</h1>
          <p className="muted">{t("pp.emptyBody")}</p>
        </div>
      </EmptyState>
    );
  }
  // Exactly one → render the full, tested by-id detail (no summary-shape dependency).
  if (items.length === 1) return <Proposal id={items[0]!.id} />;
  // Several → a short pick-list; each card is defensive about the optional summary fields.
  return (
    <div className="stack" data-testid="proposal-list">
      <header className="stack-tight">
        <h1>{t("pp.listTitle")}</h1>
        <p className="muted">{t("pp.listBody")}</p>
      </header>
      {items.map((p) => {
        const lead = p.offers?.find((o) => o.wasSuggested) ?? p.offers?.[0];
        return (
          <Card key={p.id} data-testid={`proposal-list-item-${p.id}`}>
            <Link to={`/proposal/${p.id}`} className="proposal-list-link">
              <span className="other-offer-name">
                {lead?.carrierDisplayName ?? t("pp.aProposal")}
              </span>
              {p.lineCode ? (
                <span className="muted small"> · {t(`pp.line.${p.lineCode}` as MessageKey)}</span>
              ) : null}
              {lead ? <span> · {money(lead.grossPremium, lead.currency)}</span> : null}
            </Link>
          </Card>
        );
      })}
    </div>
  );
}

export function Proposal({ id: idProp }: { id?: string } = {}) {
  const { t } = useT();
  const { id: paramId } = useParams();
  const id = idProp ?? paramId;
  const q = useProposal(id);

  // No id (the nav landing, no deep-link) or a 404 (not-yet / no-longer-PRESENTED — the
  // server merges both into one uniform miss) → the calm cold-start. Both read the same.
  const notFound = q.error instanceof ProblemError && q.error.status === 404;
  if (!id || notFound) {
    return (
      <EmptyState>
        <div data-testid="proposal-empty">
          <h1>{t("pp.emptyTitle")}</h1>
          <p className="muted">{t("pp.emptyBody")}</p>
        </div>
      </EmptyState>
    );
  }
  if (q.isLoading) {
    return (
      <Card data-testid="proposal-loading">
        <Skeleton lines={8} />
      </Card>
    );
  }
  if (q.error || !q.data) {
    return (
      <ErrorState>
        <p data-testid="proposal-error">{t("pp.error")}</p>
      </ErrorState>
    );
  }
  return <ProposalView proposal={q.data} t={t} />;
}

function ProposalView({ proposal, t }: { proposal: ProposalT; t: TFn }) {
  const api = usePartyApi();
  const respond = useRespond(proposal.id);
  const [submitted, setSubmitted] = useState<ResponseKind | null>(null);
  // PROP-PDF-F — the customer downloads the branded proposal-form PDF of their own proposal
  // (party_self RLS on the API). The stream is binary, so it can't ride the JSON client —
  // getBlob attaches the bearer and hands back a Blob, then downloadBlob saves it.
  const [pdf, setPdf] = useState<"idle" | "loading" | "error">("idle");
  async function downloadPdf() {
    setPdf("loading");
    try {
      const blob = await api.getBlob(`/v1/customer/recommendations/${proposal.id}/proposal.pdf`, {
        accept: "application/pdf",
      });
      downloadBlob(blob, "teklif.pdf");
      setPdf("idle");
    } catch {
      setPdf("error");
    }
  }

  const decided = proposal.status === "DECIDED";
  // N3 (#208) — plain offer-validity info. quoteValidUntil is null until the proposal is
  // DECIDED (no accept-of-record yet); we render the date only when present and NEVER a
  // countdown. The advisorNote box hides entirely when the advisor wrote none.
  const validUntil = proposal.quoteValidUntil ? dateTr(proposal.quoteValidUntil) : null;
  const advisorNote = proposal.advisorNote?.trim() ? proposal.advisorNote : null;
  // N3 (#210) — the presenting advisor's display name. When present it personalises the
  // note-box title ("{ad}'den not") and stands in for the {advisor} slot of the validity
  // + defer copy; when null we fall back to the generic "danışmanınız" (never a fake name).
  const advisorName = proposal.advisorName?.trim() ? proposal.advisorName.trim() : null;
  const advisorRef = advisorName ?? t("pp.advisorFallback");
  const advisorNoteTitle = advisorName
    ? t("pp.advisorNoteTitleNamed").replace("{advisor}", advisorName)
    : t("pp.advisorNoteTitle");
  const deferCopy = (
    validUntil ? t("pp.deferNote").replace("{date}", validUntil) : t("pp.deferNoteNoDate")
  ).replace("{advisor}", advisorRef);
  const prior = proposal.responses[0]; // most recent (server orders desc)
  const recommended = proposal.offers.find((o) => o.wasSuggested) ?? proposal.offers[0] ?? null;
  const others = proposal.offers.filter((o) => o !== recommended);
  const busy = respond.isPending;

  function send(kind: ResponseKind) {
    setSubmitted(null);
    respond.mutate({ response: kind }, { onSuccess: () => setSubmitted(kind) });
  }

  return (
    <div className="stack" data-testid="proposal">
      {/* Header — the concierge framing. Named advisor comes from the advisor-name
          enrichment (backend-queued); until it lands we show a generic presence (no fake). */}
      <header className="stack-tight">
        <h1>{t("pp.title")}</h1>
        <p className="muted">
          {t("pp.advisorGeneric")} · {t(`pp.line.${proposal.lineCode}` as MessageKey)}
        </p>
        <div className="proposal-actions" data-testid="proposal-pdf">
          <Button
            variant="secondary"
            data-testid="pdf-download"
            disabled={pdf === "loading"}
            onClick={downloadPdf}
          >
            {pdf === "loading" ? t("pp.pdfLoading") : t("pp.pdf")}
          </Button>
          {pdf === "error" ? (
            <p role="alert" className="muted small" data-testid="pdf-error">
              {t("pp.pdfError")}
            </p>
          ) : null}
        </div>
      </header>

      {/* N3 (#208) — offer-validity as calm, plain info. NO countdown, no urgency styling;
          rendered only when quoteValidUntil is present (never a fabricated date). */}
      {validUntil ? (
        <p className="muted small" data-testid="validity">
          {t("pp.validity").replace("{date}", validUntil).replace("{advisor}", advisorRef)}
        </p>
      ) : null}

      {/* Already-decided — the advisor has bound; actions close. */}
      {decided ? (
        <Card data-testid="proposal-decided">
          <Badge tone="success">{t("pp.decidedBadge")}</Badge>
          <p>{t("pp.decidedBody")}</p>
        </Card>
      ) : null}

      {/* The recommended offer, led & badged. */}
      {recommended ? (
        <Card data-testid="recommended-offer">
          <div className="offer-head">
            <Badge tone="success">⭐ {t("pp.recommended")}</Badge>
            <span className="offer-band" data-testid="recommended-band">
              {BAND_STARS[recommended.qualityBand]}{" "}
              {t(`pp.band.${recommended.qualityBand}` as MessageKey)}
            </span>
          </div>
          <h2 data-testid="recommended-carrier">{recommended.carrierDisplayName}</h2>
          <p className="offer-price" data-testid="recommended-price">
            <strong>{money(recommended.grossPremium, recommended.currency)}</strong>
            <span className="muted small"> {t("pp.perYear")}</span>
          </p>
          <OfferFactors offer={recommended} t={t} />
        </Card>
      ) : null}

      {/* "Bu öneri neden?" — the rationale hero (recommended ≠ cheapest, here's why). */}
      <Card data-testid="rationale">
        <h2>{t("pp.whyTitle")}</h2>
        {proposal.cheaperButRationale ? (
          <p className="rationale-prose" data-testid="rationale-prose">
            {proposal.cheaperButRationale}
          </p>
        ) : (
          <p className="rationale-prose">{t("pp.whyFallback")}</p>
        )}
        {/* reasonCode is an advisor-authored text field (no enumerated vocabulary at
            source) — rendered as-is when present, a quiet tag under the prose. */}
        {proposal.reasonCode ? (
          <p className="muted small" data-testid="rationale-reason">
            {proposal.reasonCode}
          </p>
        ) : null}
      </Card>

      {/* N3 (#210) — the advisor's personal note to the customer, under the rationale.
          Titled "{advisorName}'den not" when the presenting advisor's display name resolves,
          else the generic "Danışmanınızdan not". Hidden entirely when advisorNote is null/empty. */}
      {advisorNote ? (
        <Card data-testid="advisor-note">
          <h2>{advisorNoteTitle}</h2>
          <p className="rationale-prose" data-testid="advisor-note-body">
            {advisorNote}
          </p>
        </Card>
      ) : null}

      {/* The other offers — honestly available, secondary, rank-ordered. */}
      {others.length > 0 ? (
        <Card data-testid="other-offers">
          <details>
            <summary>{t("pp.othersTitle")}</summary>
            <ul className="other-offers">
              {others.map((o) => (
                <li key={o.carrierId} className="other-offer" data-testid={`offer-${o.carrierId}`}>
                  <span className="other-offer-name">{o.carrierDisplayName}</span>
                  <span className="muted small">
                    {BAND_STARS[o.qualityBand]} {t(`pp.band.${o.qualityBand}` as MessageKey)}
                  </span>
                  <span>{money(o.grossPremium, o.currency)}</span>
                </li>
              ))}
            </ul>
          </details>
        </Card>
      ) : null}

      {/* The best-of-panel disclosure — a legal-must display on this screen (Memo 6).
          The FINAL wording is a counsel deliverable (W1) that will land as a versioned
          notice (product/specs/disclosure-ux-placement-map.md); this is the working copy,
          flagged — not a final legal claim. */}
      <p className="muted small" role="note" data-testid="best-of-panel">
        {t("pp.disclosure")}
      </p>

      {/* Read-back — the customer's own prior intent (append-only history). */}
      {prior ? (
        <p className="muted" data-testid="readback">
          {t(`pp.readback.${prior.response}` as MessageKey)} · {dateTr(prior.respondedAt)}
        </p>
      ) : null}

      {/* The honest post-response state — "advisor will confirm", NEVER "covered". */}
      {submitted ? (
        <Card data-testid="response-success">
          {submitted === "DEFERRED" ? (
            // N3 (#208) — the calmer "daha sonra" state: no rush, valid-until date (when
            // present), and the honest "the advisor won't call; you answer here". No countdown.
            <p data-testid="defer-note">{deferCopy}</p>
          ) : (
            <p data-testid={`success-${submitted}`}>{t(`pp.success.${submitted}` as MessageKey)}</p>
          )}
        </Card>
      ) : null}

      {/* The four responses → I1 (disabled once the advisor has bound). */}
      {!decided ? (
        <Card data-testid="responses">
          <div className="response-actions">
            <Button
              variant="primary"
              data-testid="respond-accept"
              disabled={busy}
              onClick={() => send("ACCEPTED")}
            >
              {t("pp.accept")}
            </Button>
            <Button
              variant="secondary"
              data-testid="respond-defer"
              disabled={busy}
              onClick={() => send("DEFERRED")}
            >
              {t("pp.defer")}
            </Button>
          </div>
          {/* Dalga-1: in-app mesajlaşma yok — sahte "Danışmana soru sor" CTA yerine
              dürüst, uca çıkmayan bilgi. Karar butonları (kabul/ertele/vazgeç) kalır. */}
          <p className="muted small" data-testid="respond-ask-note">
            {t("pp.askNote")}
          </p>
          {/* A DECISION ON A LIVE OFFER MUST NOT VANISH. All three responses fired
              and rendered neither an error nor the success card, so a customer who
              accepted a proposal against a 409 (already decided, expired) saw the
              same screen as before and could not tell whether their acceptance was
              recorded. */}
          <MutationError
            error={respond.error}
            fallback={t("pr.respondFailed")}
            offline={t("pr.offline")}
          />
          {/* Decline — low emphasis; no free-text reason (§9-Q3). */}
          <button
            className="link-quiet"
            data-testid="respond-decline"
            disabled={busy}
            onClick={() => send("DECLINED")}
          >
            {t("pp.decline")}
          </button>
        </Card>
      ) : null}
    </div>
  );
}

// The four Suitability components — rendered from the projection (server-scored), never
// computed. Optional detail, tucked in a native disclosure to keep the column calm.
function OfferFactors({ offer, t }: { offer: ProposalOffer; t: TFn }) {
  const factors: { key: MessageKey; value: string }[] = [
    { key: "pp.factor.needsFit", value: offer.needsFit },
    { key: "pp.factor.carrierQuality", value: offer.carrierQuality },
    { key: "pp.factor.coverageQualityVsNeeds", value: offer.coverageQualityVsNeeds },
    { key: "pp.factor.priceValue", value: offer.priceValue },
  ];
  return (
    <details className="offer-factors" data-testid="offer-factors">
      <summary>{t("pp.factorsTitle")}</summary>
      <ul>
        {factors.map((f) => (
          <li key={f.key}>
            <span className="muted small">{t(f.key)}</span>
            <span>{f.value}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}
